import React, { useState, useEffect } from "react";
import CustomPopupForm from "../customPopupModel";
import BasicCustomTable from "../basicTable";
import CustomButton from "../elements/CustomButton/CustomButton";
import { useSnackbar } from "../SnackbarContext";
import axiosInstance from "../../configuration/request";
import { permissionColumns } from "../../constants/rbacManagement";
import { getRbscManagementPermissions } from "../../utils/rolesAndPermissions";
import { useSelector } from "react-redux";

const DIALOG_TYPES = {
  CREATE: "create-permission",
  DELETE: "delete",
};

function Permissions() {
  const [permissionsArray, setPermissionsArray] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [mode, setMode] = useState("create");
  const [selectedRow, setSelectedRow] = useState(null);
  const permissions = useSelector((state) => state.counter.permissions);
  const rolePermissions = permissions?.[0]?.permissions || [];
  const {  canEdit, canCreate, canDelete } =
    getRbscManagementPermissions(rolePermissions);

  useEffect(() => {
    fetchPermissions();
  }, []);

  const finalColumns = React.useMemo(() => {
    if (!canEdit && !canDelete) {
      // remove the 'action' column
      return permissionColumns.filter((col) => col.field !== "action");
    }
    return permissionColumns;
  }, [canEdit, canDelete]);

  const fetchPermissions = async () => {
    try {
      const res = await axiosInstance.get("auth/rbac/permissions");
      const formattedPermissions = res.data.map((item) => ({
        id: item.id,
        resource: item.resource,
        scopes: item.action.join(","),
        action: item.action,
      }));
      setPermissionsArray(formattedPermissions);
    } catch (error) {
      console.error("Fetch Error:", error);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRow(null);
    setDialogType("");
  };

  const handleCreate = () => {
    setMode("create");
    setDialogType(DIALOG_TYPES.CREATE);
    setOpenDialog(true);
  };

  const handleEdit = (row) => {
    setMode("edit");
    setSelectedRow(row);
    setDialogType(DIALOG_TYPES.CREATE);
    setOpenDialog(true);
  };

  const handleDelete = (row) => {
    setSelectedRow(row);
    setDialogType(DIALOG_TYPES.DELETE);
    setOpenDialog(true);
  };

  const handleDeleteConfirm = async (id) => {
    try {
      await axiosInstance.delete(`auth/rbac/delete-permission/${id}`);
      fetchPermissions();
    } catch (error) {
      console.error("Delete Error:", error);
    } finally {
      handleCloseDialog();
    }
  };

  const handleSubmit = () => {
    handleCloseDialog();
    fetchPermissions();
  };

  return (
    <div className="px-4 pb-2">
      <div className="flex justify-end my-2 pr-1">
        {canCreate && (
          <CustomButton
            onClick={handleCreate}
            customeClass="gradient-button w-[140px] h-[45px]"
          >
            <span className="text-xl mr-[4px]">+</span>Create
          </CustomButton>
        )}
      </div>

      <BasicCustomTable
        columns={finalColumns}
        rows={permissionsArray}
        colorHeader={false}
        onEdit={handleEdit}
        onDelete={handleDelete}
        customClassHeader="table-header-center"
        tableName="permission"
        frontendPagination={true}
         pageLimit={10}
        maxHeightClass="max-h-[calc(100vh-325px)]"
        canEdit={canEdit}
        canDelete={canDelete}
      />

      {dialogType === DIALOG_TYPES.CREATE && (
        <CustomPopupForm
          isOpen={openDialog}
          handleClose={handleCloseDialog}
          dialogType={DIALOG_TYPES.CREATE}
          mode={mode}
          dialogDetails={selectedRow}
          onSubmit={handleSubmit}
        />
      )}

      {dialogType === DIALOG_TYPES.DELETE && (
        <CustomPopupForm
          isOpen={openDialog}
          handleClose={handleCloseDialog}
          dialogType={DIALOG_TYPES.DELETE}
          onDelete={handleDeleteConfirm}
          dialogDetails={{
            title: "Delete Confirmation",
            content: "Are you sure you want to delete this permission record?",
            rowData: selectedRow,
          }}
        />
      )}
    </div>
  );
}

export default Permissions;
